import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

const CLEAN = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, '');

const ALL_FORMATS = [
  'aztec', 'code_128', 'code_39', 'code_93', 'codabar',
  'data_matrix', 'ean_13', 'ean_8', 'itf', 'pdf417',
  'qr_code', 'upc_a', 'upc_e',
];

const BarcodeScanner = ({ visible, onHide, onScan }) => {
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const intervalRef = useRef(null);
  const detectorRef = useRef(null);
  const busyRef     = useRef(false);

  const [manual, setManual]       = useState('');
  const [cameraOn, setCameraOn]   = useState(false);
  const [supported, setSupported] = useState(true);
  const [status, setStatus]       = useState('');

  const stopCamera = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (streamRef.current)   { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    busyRef.current = false;
    setCameraOn(false);
  }, []);

  const detectTick = useCallback(async () => {
    if (busyRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < 4 || !detectorRef.current) return;

    busyRef.current = true;
    try {
      const barcodes = await detectorRef.current.detect(video);
      if (barcodes.length > 0) {
        const code = CLEAN(barcodes[0].rawValue);
        if (code) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          stopCamera();
          onScan(code);
          return;
        }
      }
    } catch { /* ignore individual frame errors */ }
    busyRef.current = false;
  }, [stopCamera, onScan]);

  const startCamera = useCallback(async () => {
    setStatus('Pokretanje kamere...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setCameraOn(true);

      if (!('BarcodeDetector' in window)) {
        setStatus('Kamera aktivna — BarcodeDetector nije podržan u ovom pregledaču. Koristite ručni unos.');
        return;
      }

      let formats = ALL_FORMATS;
      try {
        const sup = await window.BarcodeDetector.getSupportedFormats();
        formats = ALL_FORMATS.filter(f => sup.includes(f));
      } catch { /* use full list */ }

      detectorRef.current = new window.BarcodeDetector({ formats });
      setStatus('Skeniranje aktivno — prinesite barkod kameri...');
      intervalRef.current = setInterval(detectTick, 300);

    } catch {
      setSupported(false);
      setStatus('Kamera nije dostupna. Koristite ručni unos ili USB čitač.');
    }
  }, [detectTick]);

  useEffect(() => {
    if (visible) {
      setManual('');
      setStatus('');
      setSupported(true);
      startCamera();
    } else {
      stopCamera();
    }
    return stopCamera;
  }, [visible]); // eslint-disable-line

  const submitManual = () => {
    const code = CLEAN(manual);
    if (code) { stopCamera(); onScan(code); }
  };

  const handleKey = (e) => { if (e.key === 'Enter') submitManual(); };

  return (
    <Dialog
      visible={visible}
      onHide={() => { stopCamera(); onHide(); }}
      header="Skeniraj barkod"
      style={{ width: '95vw', maxWidth: '420px' }}
      contentStyle={{ padding: '1rem' }}
      modal
    >
      {/* Camera preview */}
      <div style={{
        position: 'relative',
        marginBottom: '0.8rem',
        background: '#000',
        borderRadius: 8,
        overflow: 'hidden',
        aspectRatio: '4 / 3',
        width: '100%',
      }}>
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: cameraOn ? 'block' : 'none', borderRadius: 8 }}
          muted
          playsInline
          autoPlay
        />

        {cameraOn && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {/* scan line */}
            <div style={{
              position: 'absolute', left: '8%', right: '8%',
              height: 2, background: 'rgba(229,57,53,0.85)',
              animation: 'tmsScanLine 1.6s ease-in-out infinite alternate',
            }} />
            {/* corner brackets */}
            {['topLeft','topRight','botLeft','botRight'].map(c => (
              <div key={c} style={{
                position: 'absolute',
                width: 28, height: 28,
                borderColor: '#fff',
                borderStyle: 'solid',
                borderWidth: c.includes('top') ? '3px 0 0 3px' : '0 0 3px 3px',
                ...(c.includes('Right') ? { right: '8%' } : { left: '8%' }),
                ...(c.includes('top')   ? { top: '10%' }  : { bottom: '10%' }),
              }} />
            ))}
          </div>
        )}

        {!cameraOn && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>
            <i className="pi pi-camera" style={{ fontSize: '3rem' }} />
          </div>
        )}
      </div>

      {/* Status */}
      {status && (
        <p style={{
          fontSize: '0.82rem',
          color: status.includes('aktivno') ? '#4caf50' : '#888',
          margin: '0 0 0.8rem',
          textAlign: 'center',
        }}>
          {status}
        </p>
      )}

      {/* Manual / USB input */}
      <div className="fakture-stavke-radnje">
        <span className="p-input-icon-left" style={{ flex: 1 }}>
          <i className="pi pi-barcode" />
          <InputText
            value={manual}
            onChange={e => setManual(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            onKeyDown={handleKey}
            placeholder="Ručni unos ili USB čitač..."
            style={{ width: '100%', fontSize: '1rem', height: '44px' }}
            autoFocus={!supported}
            maxLength={50}
          />
        </span>
        <Button
          icon="pi pi-arrow-right"
          onClick={submitManual}
          disabled={!manual.trim()}
          style={{ height: '44px', width: '44px' }}
        />
      </div>

      <style>{`
        @keyframes tmsScanLine {
          from { top: 15%; }
          to   { top: 85%; }
        }
      `}</style>
    </Dialog>
  );
};

export default BarcodeScanner;
